
import { useCallback } from "react";
import { useTranslation } from "next-i18next";
import { useRequestCors } from "@/hooks/useRequestCors";
import {
  genResWithOK,
  genResWithError,
} from "@/lib/api-utils";
import { removeBase64InProject } from "@/lib/project-utils";
import { PromiseWithResolver } from "@/lib/promise-with-resolver";
import {
  useExportAudioMutation,
  useGetExportAudioMutation,
  useGetExportAudioSplitMutation,
} from "@/redux/api/projectApi";
import md5 from "md5";
import type { Dispatch, SetStateAction } from "react";
import type { ProjectType } from "@/types/type";
import type { RestApiResponse } from "@/types/common.api";
import { StreamingAudio } from "@/lib/streaming-audio";
import type { TrimmedAudioElement } from "@/lib/trimmed-media";

type PreviewAudio = {
  downloadUrl: string;
  duration: number;
}

type Cache = {
  key: string;
  value: {
    created: number;
    revision?: number;
    data: PreviewAudio;
  }
}

type Storage = {
  [key: string]: Cache["value"];
}

export type Buffering = {
  [key: string]: {
    audios: string[];
    list: {
      output_audio_path: string;
      language: string;
      script: string;
      effectScript: string;
    }[];
    finish: boolean;
    duration: number | null;
    error?: boolean;
  }
}

export type BufferingAudio = Dispatch<SetStateAction<Buffering>>

type Split = {
  type: "streaming";
  target: StreamingAudio | TrimmedAudioElement;
} | {
  type: "buffering";
  target: BufferingAudio;
}

// 구조 변경 시 revision 값 변경일로 수정
const revision = 2024100401;

const handleStorage = {
  storageKey: "preview-audio-cache",
  get: function (): Storage {
    return JSON.parse(localStorage.getItem(this.storageKey) || "{}");
  },
  set: function (item: { [key: Cache["key"]]: Cache["value"] }) {
    const [key, value] = Object.entries(item)[0];
    const list = this.get();
    list[key] = value;
    localStorage.setItem(this.storageKey, JSON.stringify(list));
  },
  clean: function () {
    const storage = this.get();
    const cleaned = Object.entries(storage).reduce(
      (acc: Storage, [key, value]) => {
        if (
          !value.revision ||
          (value.revision && value.revision < revision) ||
          Date.now() - value.created >= 1000 * 60 * 60 * 24
        ) {
          return acc;
        }
        return { ...acc, [key]: value };
      },
      {}
    );

    localStorage.setItem(this.storageKey, JSON.stringify(cleaned));
  }
};

function sleep(timeToDelay: number, abort?: AbortSignal | null) {
  return new Promise((resolve, reject) => {
    let timeout: ReturnType<typeof setTimeout> | null = null;

    function onAbort() {
      if (timeout) {
        clearTimeout(timeout);
      }
      abort?.removeEventListener("abort", onAbort);
      reject(new Error("getPreviewAudioCore aborted"));
    }

    if (abort?.aborted) {
      return onAbort();
    }
    abort?.addEventListener("abort", onAbort);

    timeout = setTimeout(() => {
      abort?.removeEventListener("abort", onAbort);
      resolve(true);
    }, timeToDelay);
  })
};

function getPreviewAudioCache(project: ProjectType, index: number) {
  handleStorage.clean();
  const target: ProjectType = removeBase64InProject(project);
  const storage = handleStorage.get();
  const key = index !== -1 && target.scenes[index]
    ? md5(JSON.stringify({
        scripts: target.scenes[index].scripts.map((script) => ({
          ...script,
          org: script.org
            .replaceAll(/\<effect.*?\>.*?\<\/effect\>/ig, "<effect></effect>"),
        })),
        dictionary: target.dictionary,
      }))
    : null;
  const value: Cache["value"] | null = key && Object.hasOwn(storage, key)
    ? storage[key]
    : null;

  return { key, value };
};

function checkPreviewCache(value: Cache["value"]) {
  if (
    value &&
    value.revision && value.revision >= revision &&
    Date.now() - value.created < 1000 * 60 * 60 * 24
  ) {
    return true;
  }
  return false;
};

function setPreviewCache(key: Cache["key"] | null, data: PreviewAudio) {
  if (key) {
    handleStorage.set({
      [key]: {
        data: data,
        revision: revision,
        created: Date.now(),
      }
    });
  }
}

export function getAudioDuration(
  url: string,
  requestCors: (url: string) => Promise<string | null>
) {
  const { promise, resolve } = new PromiseWithResolver<number | null>();

  if (!url) {
    return Promise.resolve(null);
  }

  requestCors(url).then((objectUrl) => {
    if (!objectUrl) {
      return null;
    }

    const element = new Audio(objectUrl);
    const events: { [key: string]: () => void }[] = [];

    function cleanUp() {
      events.forEach((event) => {
        const [eventName, eventCallback] = Object.entries(event)[0];
        element.removeEventListener(eventName, eventCallback);
      })
      element.pause();
      element.remove();
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    }
  
    function onLoadedMetadata() {
      resolve(element.duration);
      cleanUp();
    }
  
    function onError() {
      resolve(null);
      cleanUp();
    }
  
    element.addEventListener("error", onError);
    events.push({ error: onError });
    element.addEventListener("loadedmetadata", onLoadedMetadata);
    events.push({ loadedmetadata: onLoadedMetadata });
    element.load();
  });

  return promise;
}

export function usePreviewAudio() {
  const { t } = useTranslation("common");
  const [fetchExportAudio] = useExportAudioMutation();
  const [getExportAudio] = useGetExportAudioMutation();
  const [getExportAudioSplit] = useGetExportAudioSplitMutation();
  const { requestCors } = useRequestCors();

  const requestLivePreviewAudio = useCallback(async (params: {
    target: ProjectType;
    index: number;
    exportPromise: boolean;
  }) => {
    const { target, index, exportPromise } = params;

    const livePreviewAudio = await fetchExportAudio({
      project: {
        _id: target._id,
        scenes: target.scenes.map((scene) => ({
          ...scene,
          tempo: "1",
        })),
        userId: target.userId,
        dictionary: target.dictionary,
      },
      sceneId: index,
      isConcat: index === -1,
      exportPromise,
    }).unwrap();

    if (!livePreviewAudio.success) {
      throw new Error("fetchExportAudio fail");
    }

    if (livePreviewAudio.success && livePreviewAudio.data.success) {
      return livePreviewAudio.data.task_id;
    }
  }, [fetchExportAudio]);

  const triggerExportAudio: (params: {
    target: ProjectType;
    index: number;
    key: string | null;
    abort: AbortSignal | null;
  }) => Promise<RestApiResponse<PreviewAudio>> = useCallback(async (params) => {
    try {
      const { target, index, key, abort } = params;
  
      const result = await getExportAudio({
        projectId: target._id, sceneId: index
      }).unwrap();
  
      if (!result.success) {
        throw new Error("getExportAudio fail");
      }
  
      if (result.data.state === "complete") {
        const downloadUrl = result.data.downloadUrls[0].output_audio_path;
        const duration = result.data.downloadUrls[0].metadata?.format?.duration || 0;
        const data = {
          downloadUrl,
          duration,
        }
  
        setPreviewCache(key, data);
  
        return genResWithOK(data);
      }
        
      if (result.data.state === "error") {
        throw new Error();
      }
  
      try {
        await sleep(3000, abort);
      } catch (e) {
        const abortError = new Error();
        abortError.name = "usePreviewAudioAbort";
        throw abortError;
      }

      return await triggerExportAudio({ target, index, key, abort });
    } catch (e) {
      if (!(e instanceof Error && e.name === "usePreviewAudioAbort")) {
        console.error(e);
      }
      return genResWithError(new Error(t("The voice synthesis server is temporarily busy. Please try again later.")));
    }
  }, [getExportAudio, t]);

  const triggerExportAudioSplit: (params: {
    target: ProjectType;
    index: number;
    key: string | null;
    abort: AbortSignal | null;
    split: Split | null;
    taskId?: string | null;
    prevAudios?: string[];
  }) => Promise<RestApiResponse<PreviewAudio>> = useCallback(async (params) => {
    const {
      target,
      index,
      key,
      abort,
      split,
      taskId = null,
      prevAudios = [],
    } = params;

    if (!split) {
      throw new Error();
    }

    try {
      const result = await getExportAudioSplit({
        projectId: target._id,
        sceneId: index,
        taskId,
      }).unwrap();

      if (!result.success) {
        throw new Error();
      }

      const downloadUrls = result.data.split.downloadUrls
      const outputAudioPaths = downloadUrls.map(
        (item: { output_audio_path: string; }) => item.output_audio_path
      );

      if (prevAudios.some((path) => !outputAudioPaths.includes(path))) {
        // throw new Error();

        if (split.type === "streaming") {
          split.target.src = [];
        }
        if (split.type === "buffering") {
          split.target((prev) => ({
            ...prev,
            [String(index)]: {
              ...prev[String(index)],
              audios: [],
              list: [],
            }
          }));
        }
      }

      if (split.type === "streaming") {
        split.target.src = outputAudioPaths;
        split.target.playbackRate = target.scenes[index].tempo
          ? Number(target.scenes[index].tempo)
          : 1;
      }
      if (split.type === "buffering") {
        split.target((prev) => ({
          ...prev,
          [String(index)]: {
            ...prev[String(index)],
            audios: outputAudioPaths,
            list: downloadUrls,
            tempo: target.scenes[index].tempo
              ? Number(target.scenes[index].tempo)
              : 1,
          }
        }));
      }

      if (result.data.split.isFinish) {
        if (split.type === "streaming" && split.target instanceof StreamingAudio && split.target.buffering) {
          split.target.buffer.end();
        }
        if (split.type === "buffering") {
          split.target((prev) => ({
            ...prev,
            [String(index)]: {
              ...prev[String(index)],
              finish: true,
            }
          }));
        }
      }

      if (result.data.export.state === "complete") {
        const downloadUrl = result.data.export.downloadUrls[0].output_audio_path;
        const duration = result.data.export.downloadUrls[0].metadata?.format?.duration || 0;
        const data = {
          downloadUrl,
          duration,
        }
        
        setPreviewCache(key, data);

        if (downloadUrls.length === 0) {
          if (split.type === "streaming") {
            split.target.src = [downloadUrl];
            split.target.playbackRate = target.scenes[index].tempo
              ? Number(target.scenes[index].tempo)
              : 1;
          }
          if (split.type === "buffering") {
            const scripts = target.scenes[index].scripts
            const mergedScript =
              target.scenes[index].scripts
                .map((script) => script.org)
                .join('') || ''
            const cleanedScript = mergedScript.replace(/<[^>]*>/g, '')

            split.target((prev) => ({
              ...prev,
              [String(index)]: {
                ...prev[String(index)],
                audios: [downloadUrl],
                list: [{
                  output_audio_path: downloadUrl,
                  effectScript: mergedScript,
                  language: scripts[0].detectedLanguage || 'en',
                  script: cleanedScript,
                }],
                tempo: target.scenes[index].tempo
                  ? Number(target.scenes[index].tempo)
                  : 1,
              }
            }));
          }
        }
        
        if (split.type === "streaming" && split.target instanceof StreamingAudio) {
          if (split.target.buffering) {
            split.target.buffer.end();
          }
        }
        if (split.type === "buffering") {
          split.target((prev) => ({
            ...prev,
            [String(index)]: {
              ...prev[String(index)],
              finish: true,
              duration,
            }
          }));
        }

        return genResWithOK(data);
      }
      
      if (result.data.export.state === "error") {
        throw new Error();
      }
  
      try {
        await sleep(1000, abort);
      } catch (e) {
        const abortError = new Error();
        abortError.name = "usePreviewAudioAbort";
        throw abortError;
      }

      return await triggerExportAudioSplit({
        target,
        index,
        key,
        abort,
        split,
        taskId,
        prevAudios: outputAudioPaths,
      });
    } catch (e) {
      if (e instanceof Error && e.name === "usePreviewAudioAbort") {
        return genResWithError({});
      }

      console.error(e);

      if (split) {
        if (split.type === "streaming" && split.target instanceof StreamingAudio) {
          split.target.src = [];
          split.target.error = new Error();
          if (split.target.buffering) {
            split.target.buffer.end();
          }
        }
        if (split.type === "buffering") {
          split.target((prev) => ({
            ...prev,
            [String(index)]: {
              audios: [],
              list: [],
              finish: true,
              duration: null,
              error: true,
            }
          }));
        }
      }
      return genResWithError(new Error(t("The voice synthesis server is temporarily busy. Please try again later.")));
    }
  }, [getExportAudioSplit, t]);

  const getLivePreviewAudio: (params: {
    target: ProjectType;
    index: number;
    key: string | null;
    abort: AbortSignal | null;
    split: Split | null,
    taskId: string,
    exportPromise: boolean,
  }) => Promise<RestApiResponse<PreviewAudio>> = useCallback(async (params) => {
    const { target, index, key, abort, split, taskId, exportPromise } = params;

    if (exportPromise && split)
      return triggerExportAudioSplit({ target, index, key, abort, split, taskId });

    if (split) {
      return triggerExportAudioSplit({ target, index, key, abort, split });
    }

    return triggerExportAudio({ target, index, key, abort });
  }, [triggerExportAudio, triggerExportAudioSplit]);

  const getPreviewAudio: (params: {
    project: ProjectType,
    index: number,
    abort?: AbortSignal | null,
    split?: Split | null,
    exportPromise?: boolean,
  }) => Promise<RestApiResponse<PreviewAudio>> = useCallback(
    async (params) => {
      const {
        project,
        index,
        abort = null,
        split = null,
        exportPromise = false,
      } = params;

      try {
        const target: ProjectType = removeBase64InProject(project);

        if (index !== -1 && !target.scenes[index]) {
          throw new Error("project or scene is null");
        }

        if (split && index === -1) {
          throw new Error("The index is incorrect.");
        }

        if (split) {
          if (split.type === "streaming" && split.target instanceof StreamingAudio) {
            split.target.buffer.start();
          }
          if (split.type === "buffering") {
            split.target((prev) => ({
              ...prev,
              [String(index)]: {
                audios: [],
                list: [],
                finish: false,
                duration: null,
                error: false,
              }
            }));
          }
        }

        if (
          target.scenes[index].scripts.some((script) => (
            script.tts?.type === "audio"
          ))
        ) {
          const tts: Tts = target.scenes[index].scripts.find((script) => (
            script.tts?.type === "audio"
          ))?.tts;
          const url = tts?.url;

          if (!url) {
            throw new Error();
          }

          const duration = tts.trim
            ? tts.trim.reduce((acc: number, item) => {
              return acc + item.end - item.start;
            }, 0)
            : await getAudioDuration(url, requestCors) || 0;

          if (split) {
            if (split.type === "streaming") {
              if (split.target instanceof StreamingAudio) {
                split.target.src = [url];
                split.target.playbackRate = 1;
                split.target.buffer.end();
              } else {
                split.target.src = url;
              }
            }
            if (split.type === "buffering") {
              split.target((prev) => ({
                ...prev,
                [String(index)]: {
                  audios: [url],
                  /**
                   * 오디오 스크립트이기 때문에 script, effectScript 등은 비어 있어도 괜찮음
                   */
                  list: [{
                    output_audio_path: url,
                    effectScript: '',
                    language: 'en',
                    script: '',
                  }],
                  finish: true,
                  duration,
                }
              }));
            }
          }

          return genResWithOK({
            downloadUrl: url,
            duration,
          });
        }

        const { key, value } = getPreviewAudioCache(target, index);
        
        if (value && checkPreviewCache(value)) {
          if (split) {
            if (split.type === "streaming" && split.target instanceof StreamingAudio) {
              split.target.src = [value.data.downloadUrl];
              split.target.playbackRate = target.scenes[index].tempo
                ? Number(target.scenes[index].tempo)
                : 1;
              split.target.buffer.end();
            }
            if (split.type === "buffering") {
              const scripts = target.scenes[index].scripts
              const mergedScript =
                target.scenes[index].scripts
                  .map((script) => script.org)
                  .join('') || ''
              const cleanedScript = mergedScript.replace(/<[^>]*>/g, '')

              split.target((prev) => ({
                ...prev,
                [String(index)]: {
                  audios: [value.data.downloadUrl],
                  list: [{
                    output_audio_path: value.data.downloadUrl,
                    effectScript: mergedScript,
                    language: scripts[0].detectedLanguage || 'en',
                    script: cleanedScript,
                  }],
                  finish: true,
                  duration: value.data.duration || 0,
                  tempo: target.scenes[index].tempo
                    ? Number(target.scenes[index].tempo)
                    : 1,
                }
              }));
            }
          }

          return genResWithOK(value.data);
        }

        const taskId = await requestLivePreviewAudio({ target, index, exportPromise });

        return await getLivePreviewAudio({ target, index, key, abort, split, taskId, exportPromise });
      } catch (e) {
        console.error(e);
        if (split) {
          if (split.type === "streaming" && split.target instanceof StreamingAudio) {
            split.target.error = new Error();
            split.target.buffer.end();
          }
          if (split.type === "buffering") {
            split.target((prev) => ({
              ...prev,
              [String(index)]: {
                audios: [],
                list: [],
                finish: true,
                duration: null,
                error: true,
              }
            }));
          }
        }
        return genResWithError(new Error(t("The voice synthesis server is temporarily busy. Please try again later.")));
      }
    },
    [getLivePreviewAudio, requestCors, requestLivePreviewAudio, t]
  );

  return { getPreviewAudio };
}
